/**
 * WP-9 audio engine — a thin WebAudio playback layer over the pure synth
 * (audio/synth.ts). Lazily creates one AudioContext (on the first user gesture,
 * per the browser autoplay policy), caches each cue as an AudioBuffer, and plays
 * one-shot SFX through an sfx bus and a looping score through a music bus, both
 * under a master gain driven by the persisted volume / mute (ui/save.ts).
 *
 * Degrades gracefully: if no AudioContext exists (or construction throws) every
 * method is a silent no-op, so the game runs fine without sound.
 */
import { click, explosion, fanfare, fireFor, music, sink, splash, type Pcm } from './synth';
import type { ShipType } from '../rules/state';
import { getMuted, getVolume, setMuted, setVolume } from '../ui/save';

type AudioCtor = typeof AudioContext;

class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private readonly buffers = new Map<string, AudioBuffer>();
  private musicSrc: AudioBufferSourceNode | null = null;
  private muted = getMuted();
  private volume = getVolume();

  /** Create the context + bus graph (idempotent). Safe to call before any gesture. */
  init(): void {
    if (this.ctx) return;
    const Ctor: AudioCtor | undefined =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: AudioCtor }).webkitAudioContext;
    if (!Ctor) return;
    try {
      const ctx = new Ctor();
      const master = ctx.createGain();
      master.gain.value = this.muted ? 0 : this.volume;
      master.connect(ctx.destination);
      const musicGain = ctx.createGain();
      musicGain.gain.value = 0.85;
      musicGain.connect(master);
      const sfxGain = ctx.createGain();
      sfxGain.gain.value = 1;
      sfxGain.connect(master);
      this.ctx = ctx;
      this.master = master;
      this.musicGain = musicGain;
      this.sfxGain = sfxGain;
    } catch {
      this.ctx = null; // no audio available — every method no-ops
    }
  }

  /** Resume a suspended context (call from a user-gesture handler). */
  resume(): void {
    this.init();
    if (this.ctx && this.ctx.state === 'suspended') void this.ctx.resume();
  }

  private toBuffer(key: string, make: () => Pcm): AudioBuffer | null {
    if (!this.ctx) return null;
    let b = this.buffers.get(key);
    if (!b) {
      const p = make();
      b = this.ctx.createBuffer(1, p.data.length, p.sampleRate);
      b.getChannelData(0).set(p.data);
      this.buffers.set(key, b);
    }
    return b;
  }

  private oneShot(buf: AudioBuffer | null, gain = 1): void {
    if (!this.ctx || !this.sfxGain || !buf) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    if (gain === 1) {
      src.connect(this.sfxGain);
    } else {
      const g = this.ctx.createGain();
      g.gain.value = gain;
      src.connect(g);
      g.connect(this.sfxGain);
    }
    src.start();
  }

  playFire(type: ShipType): void {
    this.resume();
    this.oneShot(this.toBuffer(`fire:${type}`, () => fireFor(type)));
  }
  playExplosion(): void {
    this.resume();
    this.oneShot(this.toBuffer('explosion', explosion));
  }
  playSink(): void {
    this.resume();
    this.oneShot(this.toBuffer('sink', sink));
  }
  playSplash(): void {
    this.resume();
    this.oneShot(this.toBuffer('splash', splash), 0.8);
  }
  playClick(): void {
    this.resume();
    this.oneShot(this.toBuffer('click', click), 0.7);
  }
  playFanfare(): void {
    this.resume();
    this.oneShot(this.toBuffer('fanfare', fanfare));
  }

  startMusic(): void {
    this.resume();
    if (!this.ctx || !this.musicGain || this.musicSrc) return;
    const buf = this.toBuffer('music', music);
    if (!buf) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.connect(this.musicGain);
    src.start();
    this.musicSrc = src;
  }
  stopMusic(): void {
    if (!this.musicSrc) return;
    try {
      this.musicSrc.stop();
    } catch {
      /* already stopped */
    }
    this.musicSrc.disconnect();
    this.musicSrc = null;
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    setVolume(this.volume);
    if (this.master && !this.muted) this.master.gain.value = this.volume;
  }
  getVolume(): number {
    return this.volume;
  }

  /** Flip mute; returns the new muted state. */
  toggleMute(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }
  setMuted(on: boolean): void {
    this.muted = on;
    setMuted(on);
    if (this.master) this.master.gain.value = on ? 0 : this.volume;
  }
  isMuted(): boolean {
    return this.muted;
  }

  /** Inspection for the E2E harness. */
  debug(): {
    ready: boolean; buffers: number; music: boolean; muted: boolean; volume: number; state: string;
  } {
    return {
      ready: !!this.ctx,
      buffers: this.buffers.size,
      music: !!this.musicSrc,
      muted: this.muted,
      volume: this.volume,
      state: this.ctx?.state ?? 'none',
    };
  }
}

export const audio = new AudioEngine();
export type { AudioEngine };
